import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import io from "socket.io-client";
import toast from "react-hot-toast";
import moment from "moment";
import { Formik, Form, FieldArray, Field, useFormikContext } from "formik";
import "../../styles/LiveSession.css";
import "../../styles/MobileLiveSession.css";
import { AuthContext } from "../../context/AuthContext";
import LiveSessionServices from "../../services/LiveSessionServices";
import SettingServices from "../../services/SettingServices";
import Loader from "../../components/Loader";
import NoData from "../../components/NoData";
import { TbReceiptDollar } from "react-icons/tb";
import { LuWallet } from "react-icons/lu";
import { GrLineChart } from "react-icons/gr";
import { MdArrowBackIos, MdCurrencyRupee } from "react-icons/md";
import { useHistory, useLocation, Link } from "react-router-dom";
import { FaHistory } from "react-icons/fa";
import {
  getStoredActiveGameTab,
  setStoredActiveGameTab,
} from "../../utils/utils";
import tenorGif from "../../assets/tenor.gif";
import { LIVE_YANTRA_IMAGE_URL } from "../../utils/utils";

const getYantraImage = (img) =>
  img
    ? `${LIVE_YANTRA_IMAGE_URL}/${img}`
    : `${LIVE_YANTRA_IMAGE_URL}/default_image.png`;

/** Memoizes yantra image URLs and preloads images. Must be used in a component (not callback). */
const YantraImagesLoader = ({ values, children }) => {
  const yantraImages = useMemo(
    () =>
      (values?.yantra_list || []).map((y) => ({
        ...y,
        imgSrc: getYantraImage(y.yantra_img),
      })),
    [values?.yantra_list],
  );

  useEffect(() => {
    if (!values?.yantra_list) return;
    values.yantra_list.forEach((y) => {
      const img = new Image();
      img.src = getYantraImage(y.yantra_img);
    });
  }, [values?.yantra_list]);

  return children(yantraImages);
};

const SyncTotalBetAmount = () => {
  const { values, setFieldValue } = useFormikContext();
  const list = values.yantra_list || [];
  const countsKey = JSON.stringify(
    list.map((y) => Number(y.yantra_count) || 0),
  );
  useEffect(() => {
    const totalBet = list.reduce(
      (sum, y) => sum + (Number(y.yantra_count) || 0),
      0,
    );
    const amount = list.reduce(
      (sum, y) =>
        sum + (Number(y.yantra_count) || 0) * (Number(y.yantra_amount) || 0),
      0,
    );
    setFieldValue("total_bet", totalBet);
    setFieldValue("amount", amount);
  }, [countsKey, setFieldValue]);
  return null;
};

const formatMoney = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0.00";
  return x.toFixed(2);
};

/** Detect landscape: Screen Orientation API, then matchMedia, then width > height */
function useIsLandscape() {
  const [isLandscape, setIsLandscape] = useState(() => {
    if (typeof window === "undefined") return true;
    if (window.screen?.orientation?.type) {
      const type = window.screen.orientation.type.toLowerCase();
      return type.includes("landscape");
    }
    if (window.matchMedia?.("(orientation: landscape)").matches) return true;
    return window.innerWidth > window.innerHeight;
  });

  useEffect(() => {
    const update = () => {
      let landscape = false;
      if (window.screen?.orientation?.type) {
        const type = String(window.screen.orientation.type).toLowerCase();
        landscape = type.includes("landscape");
      } else if (window.matchMedia?.("(orientation: landscape)").matches) {
        landscape = true;
      } else {
        landscape = window.innerWidth > window.innerHeight;
      }
      setIsLandscape(landscape);
    };

    window.addEventListener("resize", update);
    if (window.screen?.orientation?.addEventListener) {
      window.screen.orientation.addEventListener("change", update);
    }
    update();
    return () => {
      window.removeEventListener("resize", update);
      if (window.screen?.orientation?.removeEventListener) {
        window.screen.orientation.removeEventListener("change", update);
      }
    };
  }, []);

  return isLandscape;
}

/** Full-screen overlay when in portrait: blocks interaction, asks user to rotate */
const RotateOverlay = () => (
  <div className="mobile-rotate-overlay" role="alert" aria-live="polite">
    <div className="mobile-rotate-content">
      <div className="mobile-rotate-icon" aria-hidden="true">
        <svg
          width="80"
          height="80"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
          <line x1="12" y1="18" x2="12.01" y2="18" />
        </svg>
      </div>
      <h2 className="mobile-rotate-title">Please rotate your device</h2>
      <p className="mobile-rotate-text">Use landscape mode to continue</p>
    </div>
  </div>
);

const getGameTypeFromSearch = (search) => {
  const params = new URLSearchParams(search);
  const game = params.get("game") || params.get("gameType") || "";
  return game === "5min" || game === "3min" ? game : null;
};

const MobileLiveSession = () => {
  const isLandscape = useIsLandscape();
  const routerHistory = useHistory();
  const location = useLocation();
  const liveSSApi = new LiveSessionServices();
  const settingApi = new SettingServices();
  const { user, logout } = useContext(AuthContext);
  const [profile, setProfile] = useState(user);
  const [liveSessionNote1, setLiveSessionNote1] = useState("");
  const [liveSessionNote2, setLiveSessionNote2] = useState("");

  useEffect(() => {
    if (user) setProfile(user);
  }, [user]);

  useEffect(() => {
    settingApi
      .getSettingData()
      .then((response) => {
        if (response?.isSuccess && response?.data) {
          const data = response.data;
          setLiveSessionNote1(data.live_session_note1 ?? "");
          setLiveSessionNote2(data.live_session_note2 ?? "");
        }
      })
      .catch(() => {});
  }, []);

  const [activeTab, setActiveTab] = useState(() => {
    const fromUrl = getGameTypeFromSearch(location.search);
    return fromUrl || getStoredActiveGameTab();
  });
  const activeTabRef = useRef(activeTab);
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);
  useEffect(() => {
    setStoredActiveGameTab(activeTab);
  }, [activeTab]);

  const [sessionId, setSessionId] = useState(null);
  const [sessionNo, setSessionNo] = useState(null);
  const [sessionDbId, setSessionDbId] = useState(null);
  const [sessionStatus, setSessionStatus] = useState("live");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [gameStoppedByAdmin, setGameStoppedByAdmin] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const endTimeStrRef = useRef(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [currentTimeDisplay, setCurrentTimeDisplay] = useState("");
  const timerRef = useRef(null);
  const reloadInFlightRef = useRef(false);
  const lastReloadAtRef = useRef(0);
  const lastLastDrawRefreshAtRef = useRef(0);
  const sessionIdRef = useRef(null);
  const sessionDbIdRef = useRef(null);
  const profileRef = useRef(null);
  useEffect(() => {
    sessionIdRef.current = sessionId;
    sessionDbIdRef.current = sessionDbId;
    profileRef.current = profile;
  }, [sessionId, sessionDbId, profile]);

  const scrollContainerRef = useRef(null);
  const mainContentRef = useRef(null);
  const footerRef = useRef(null);
  const [isFooterOnlyVisible, setIsFooterOnlyVisible] = useState(false);

  // Place bet confirmation modal (mobile only)
  const [placeBetConfirmOpen, setPlaceBetConfirmOpen] = useState(false);
  const [placeBetConfirmData, setPlaceBetConfirmData] = useState(null);
  const placeBetConfirmProceedRef = useRef(false);

  // Win congratulations modal (mobile only)
  const [winCongratsOpen, setWinCongratsOpen] = useState(false);
  const [winCongratsData, setWinCongratsData] = useState(null);
  const lastWinShownSessionDbIdRef = useRef(null);
  const placedSessionDbIdsRef = useRef(new Set());

  const TIME_DISPLAY_FORMAT = "h:mm A";

  const buildPlaceBetConfirmData = (values) => {
    const list = Array.isArray(values?.yantra_list) ? values.yantra_list : [];
    const rows = list
      .map((y) => {
        const qty = Number(y?.yantra_count) || 0;
        const price = Number(y?.yantra_amount) || 0;
        if (qty <= 0) return null;
        return {
          name: y?.yantra_name || "Yantra",
          qty,
          price,
          amount: qty * price,
        };
      })
      .filter(Boolean);

    const totalQty = rows.reduce((sum, r) => sum + (Number(r.qty) || 0), 0);
    const totalAmount = rows.reduce(
      (sum, r) => sum + (Number(r.amount) || 0),
      0,
    );

    const purchaseTime = moment().format("DD-MM-YYYY hh:mm A");

    const gameKey = activeTabRef.current || values?.game_type || "";

    return {
      gameLabel: `Yantra Session[${gameKey}]`,
      sessionNo: values?.session_no ?? sessionNo ?? "—",
      userLabel: profile?.name
        ? `${profile.name}${profile?.game_id ? ` [${profile.game_id}]` : ""}`
        : "—",
      purchaseTime,
      currentTime: currentTimeDisplay || values?.current_time || "—",
      drawTime: values?.draw_time || "—",
      rows,
      totalQty,
      totalAmount,
    };
  };

  const maybeShowWinCongratsForSession = async (endedSessionDbId) => {
    const sid = Number(endedSessionDbId);
    if (!Number.isFinite(sid) || sid <= 0) return;
    if (!placedSessionDbIdsRef.current?.has(sid)) return;
    if (lastWinShownSessionDbIdRef.current === sid) return;

    try {
      // Wait a moment for settlement to update slip win_amount for mobile users.
      await new Promise((r) => setTimeout(r, 900));

      const res = await liveSSApi.getCurrentSessionPlaceBets(sid);
      if (!res?.isSuccess || !Array.isArray(res?.data)) return;

      // Backend has a fallback to last bet when no rows in this session.
      // Guard against that: only keep rows for this session.
      const slips = res.data.filter((x) => Number(x?.session_id) === sid);
      if (slips.length === 0) return;

      const totalPurchase = slips.reduce(
        (sum, s) => sum + (Number(s?.amount) || 0),
        0,
      );
      const totalWin = slips.reduce(
        (sum, s) => sum + (Number(s?.win_amount) || 0),
        0,
      );

      if (Number(totalWin) > 0) {
        setWinCongratsData({
          sessionNo: slips[0]?.session_no ?? slips[0]?.session_id ?? sid,
          slipCount: slips.length,
          totalPurchase,
          totalWin,
          slips: slips.map((s) => ({
            placebet_uid: s?.placebet_uid,
            amount: Number(s?.amount) || 0,
            win_amount: Number(s?.win_amount) || 0,
          })),
        });
        setWinCongratsOpen(true);
        lastWinShownSessionDbIdRef.current = sid;
      }
    } catch (_) {
      // ignore
    } finally {
      // This session is over; don't re-check it repeatedly.
      try {
        placedSessionDbIdsRef.current?.delete(sid);
      } catch (_) {}
    }
  };

  useEffect(() => {
    document.body.classList.add("mobile-live-session-body");
    return () => {
      document.body.classList.remove("mobile-live-session-body");
    };
  }, []);

  useEffect(() => {
    const scrollEl = scrollContainerRef.current;
    const mainEl = mainContentRef.current;
    const footerEl = footerRef.current;
    if (!scrollEl || !mainEl || !footerEl) return;

    const visibilityRef = { main: true, footer: false };
    const updateFooterOnly = () => {
      setIsFooterOnlyVisible(!visibilityRef.main && visibilityRef.footer);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const target = entry.target;
          const ratio = entry.intersectionRatio;
          const visible = ratio > 0;
          if (target === mainEl) visibilityRef.main = visible;
          if (target === footerEl) visibilityRef.footer = visible;
        });
        updateFooterOnly();
      },
      { root: scrollEl, threshold: [0, 0.01, 1] },
    );

    observer.observe(mainEl);
    observer.observe(footerEl);
    return () => {
      observer.disconnect();
    };
  }, []);

  const parseEndTime = (endTimeStr) => {
    if (!endTimeStr) return null;
    const s = String(endTimeStr).trim();
    if (!s) return null;
    const hasUtcMarker = s.endsWith("Z") || s.includes("T");
    const m = hasUtcMarker ? moment.utc(s) : moment(s);
    return m.isValid() ? { endM: m, isUTC: hasUtcMarker } : null;
  };

  const getRemainingSecondsFromEndTime = (endTimeStr) => {
    if (!endTimeStr) return 0;
    const secs = moment(endTimeStr).diff(moment(), "seconds");
    return Math.max(Number.isFinite(secs) ? secs : 0, 0);
  };

  const [loading, setLoading] = useState(false);
  const [yantraLoading, setYantraLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({
    wallet_balance: 0,
    paid_amount: 0,
    total_bet: 0,
    amount: 0,
    date: "",
    draw_time: "",
    remainning_time: 0,
    current_time: "",
    session_id: "",
    session_uid: "",
    session_status: "",
    game_type: "",
    start_time: "",
    end_time: "",
    percentage: "",
    winner_type: "",
    winner_count: 0,
    session_no: "",
    yantra_list: [
      {
        yantra_id: 0,
        yantra_img: null,
        yantra_name: null,
        yantra_amount: null,
        yantra_count: 0,
      },
    ],
    last_winning_amount: 0,
    winning_amount: 0,
  });

  const fetchTodayLastDraw = async (gameType = activeTab) => {
    try {
      setHistoryLoading(true);
      const response = await liveSSApi.getTodayLastDraw(gameType);
      if (response?.isSuccess && Array.isArray(response?.data)) {
        setHistory(response.data);
      } else {
        setHistory([]);
      }
    } catch (error) {
      setHistory([]);
      toast.error(error?.response?.data?.message || "Error loading last draw");
    } finally {
      setHistoryLoading(false);
    }
  };

  function getLiveSessionData(gameType = activeTab, opts = {}) {
    const { silent = false, keepCurrentOnEmpty = false } = opts;
    setLoading(true);
    return liveSSApi
      .getLiveSessionData(gameType)
      .then((response) => {
        if (response.isSuccess && response?.data) {
          const data = response?.data;
          const walletData = response?.walletData;
          const walletBalance = walletData?.wallet_balance ?? 0;
          const winningAmount = walletData?.winning_amount ?? 0;
          const lastWinningAmount = response?.lastWinningAmount ?? 0;
          const paidAmount =
            walletData?.paid_amount ?? walletData?.paid_amoun ?? 0;
          const parsedEnd = parseEndTime(data?.end_time);
          endTimeStrRef.current = data?.end_time || null;

          setSessionId(data.session_uid);
          setSessionNo(data.session_no);
          setSessionDbId(data.id);
          setSessionStatus(data.status || "live");

          const start = moment.utc(data?.start_time);
          const endM = parsedEnd?.endM || moment.utc(data?.end_time);
          const date = start.isValid()
            ? start.local().format("DD-MM-YYYY")
            : moment().format("DD-MM-YYYY");
          const drawTime = endM.isValid()
            ? endM.clone().local().format(TIME_DISPLAY_FORMAT)
            : "";
          const currentTime = moment().local().format(TIME_DISPLAY_FORMAT);
          const remaining = getRemainingSecondsFromEndTime(data?.end_time);
          setRemainingSeconds(remaining);
          setCurrentTimeDisplay(currentTime);

          setInitialValues((prev) => ({
            ...prev,
            wallet_balance: walletBalance,
            paid_amount: paidAmount,
            date,
            draw_time: drawTime,
            current_time: currentTime,
            remainning_time: remaining,
            session_id: data?.id || "",
            session_uid: data?.session_uid || "",
            session_status: data?.status || "",
            game_type: data?.game_type || "",
            start_time: data?.start_time || "",
            end_time: data?.end_time || "",
            percentage: data?.percentage || "",
            winner_type: data?.winner_type || "",
            winner_count: data?.winner_count || 0,
            session_no: data?.session_no || "",
            winning_amount: winningAmount,
            last_winning_amount: lastWinningAmount,
          }));
          setGameStoppedByAdmin(false);
        } else {
          if (!keepCurrentOnEmpty) {
            endTimeStrRef.current = null;
            setSessionId(null);
            setCurrentTimeDisplay("");
          }
          setRemainingSeconds(0);
          const stopped =
            response?.gameStopped ||
            (response?.message &&
              String(response.message).includes("stopped by admin"));
          setGameStoppedByAdmin(!!stopped);
          if (!silent) toast.error(response?.message || "No running session");
        }
      })
      .catch((error) => {
        if (!silent && !keepCurrentOnEmpty) {
          endTimeStrRef.current = null;
          setSessionId(null);
          setCurrentTimeDisplay("");
        }
        setRemainingSeconds(0);
        setGameStoppedByAdmin(false);
        if (!silent) {
          toast.dismiss();
          toast.error(
            error?.response?.data?.message ||
              (error?.response?.status === 404
                ? "No running session"
                : "Error loading session"),
          );
        }
      })
      .finally(() => setLoading(false));
  }

  const getYantraData = async () => {
    setYantraLoading(true);
    liveSSApi
      .getYantraListData()
      .then((response) => {
        if (response.isSuccess) {
          const yantras = response?.data || [];
          const mappedYantras = yantras.map((y) => ({
            yantra_id: y.id,
            yantra_name: y.yantra_name,
            yantra_img: y.yantra_img,
            yantra_amount: y.amount,
            yantra_count: 0,
          }));
          setInitialValues((prev) => ({ ...prev, yantra_list: mappedYantras }));
        } else {
          toast.error(response?.message || "Error loading yantras");
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || "Error loading yantras");
      })
      .finally(() => setYantraLoading(false));
  };

  useEffect(() => {
    getYantraData();
    getLiveSessionData();
    fetchTodayLastDraw(activeTab);
  }, [activeTab]);

  // Single socket instance: game status broadcast + session join (prevents reconnect on every state change)
  const sessionSocketRef = useRef(null);
  const lastJoinedSessionIdRef = useRef(null);

  useEffect(() => {
    if (sessionSocketRef.current) return;
    const s = io(process.env.REACT_APP_NODE_URL, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
    });
    sessionSocketRef.current = s;

    s.on("connect", () => {
      const sid = sessionIdRef.current;
      const sdbId = sessionDbIdRef.current;
      const prof = profileRef.current;
      if (sid && sdbId && prof?.user_id && prof?.name) {
        const payload = {
          sessionId: sid,
          sessionDbId: sdbId,
          userId: prof.user_id,
          username: prof.name,
          role: prof.role,
          gameType: activeTabRef.current,
        };
        s.emit("joinSession", payload);
        lastJoinedSessionIdRef.current = sid;
      }
    });

    const refreshLastDraw = () => {
      fetchTodayLastDraw(activeTabRef.current);
      setTimeout(() => fetchTodayLastDraw(activeTabRef.current), 3500);
    };

    s.on("countdown", ({ remaining, currentTime }) => {
      if (typeof remaining === "number") setRemainingSeconds(remaining);
      if (currentTime) {
        const formatted = moment
          .utc(currentTime)
          .local()
          .format(TIME_DISPLAY_FORMAT);
        setCurrentTimeDisplay(formatted);
      }
      if (typeof remaining === "number" && remaining <= 0) {
        const now = Date.now();
        if (
          !reloadInFlightRef.current &&
          now - lastReloadAtRef.current > 2000
        ) {
          reloadInFlightRef.current = true;
          lastReloadAtRef.current = now;
          Promise.resolve()
            .then(() =>
              getLiveSessionData(activeTabRef.current, {
                silent: true,
                keepCurrentOnEmpty: true,
              }),
            )
            .then(() => {
              const now2 = Date.now();
              if (now2 - lastLastDrawRefreshAtRef.current > 5000) {
                lastLastDrawRefreshAtRef.current = now2;
                fetchTodayLastDraw(activeTabRef.current);
              }
            })
            .finally(() => {
              reloadInFlightRef.current = false;
            });
        }
      }
    });

    s.on("gameResult", () => {
      const endedSessionDbId = sessionDbIdRef.current;
      getLiveSessionData(activeTabRef.current, { silent: true });
      refreshLastDraw();
      if (endedSessionDbId) maybeShowWinCongratsForSession(endedSessionDbId);
    });

    s.on("gameStatusChange", () => {
      getLiveSessionData(activeTabRef.current, { silent: true });
    });

    s.on("newRound", () => {
      getLiveSessionData(activeTabRef.current, { silent: true });
      refreshLastDraw();
    });

    s.on("sessionEnded", () => {
      const endedSessionDbId = sessionDbIdRef.current;
      getLiveSessionData(activeTabRef.current, { silent: true });
      refreshLastDraw();
      if (endedSessionDbId) maybeShowWinCongratsForSession(endedSessionDbId);
    });

    return () => {
      s.disconnect();
      sessionSocketRef.current = null;
      lastJoinedSessionIdRef.current = null;
    };
  }, []);

  // Join session when sessionId/sessionDbId change (e.g. tab switch); leave old session first
  useEffect(() => {
    const socket = sessionSocketRef.current;
    if (
      !socket ||
      !sessionId ||
      !sessionDbId ||
      !profile?.user_id ||
      !profile?.name
    )
      return;
    if (!socket.connected) return;

    const prevSessionId = lastJoinedSessionIdRef.current;
    if (prevSessionId === sessionId) return;

    if (prevSessionId) {
      socket.emit("leaveSession", { sessionId: prevSessionId });
    }
    const payload = {
      sessionId,
      sessionDbId,
      userId: profile.user_id,
      username: profile.name,
      role: profile.role,
      gameType: activeTabRef.current,
    };
    socket.emit("joinSession", payload);
    lastJoinedSessionIdRef.current = sessionId;
  }, [sessionId, sessionDbId, profile?.user_id, profile?.name, profile?.role]);

  // Fallback countdown: when socket countdown is not received (e.g. reconnecting), compute locally every 2s
  useEffect(() => {
    if (!sessionId) return;

    const tick = () => {
      const endTimeStr = endTimeStrRef.current;
      const secs = getRemainingSecondsFromEndTime(endTimeStr);
      setRemainingSeconds(secs);
      setCurrentTimeDisplay(moment().format(TIME_DISPLAY_FORMAT));
      if (
        secs <= 0 &&
        !reloadInFlightRef.current &&
        Date.now() - lastReloadAtRef.current > 2000
      ) {
        reloadInFlightRef.current = true;
        lastReloadAtRef.current = Date.now();
        getLiveSessionData(activeTabRef.current, {
          silent: true,
          keepCurrentOnEmpty: true,
        }).finally(() => {
          reloadInFlightRef.current = false;
        });
      }
    };

    tick();
    const fallbackInterval = setInterval(tick, 2000);

    return () => clearInterval(fallbackInterval);
  }, [sessionId]);

  const formatRemaining = (seconds) => {
    if (seconds == null || isNaN(seconds) || seconds < 0) return "00:00";
    const sec = Math.floor(Number(seconds));
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const handleBack = () => routerHistory.push("/dashboard");
  const [quickAmount, setQuickAmount] = useState(1);
  const AMOUNT_CHIPS = [1, 2, 5, 10, 20];

  if (!isLandscape) {
    return (
      <div className="mobile-live-root mobile-live-portrait">
        <RotateOverlay />
      </div>
    );
  }

  return (
    <div className="mobile-live-root">
      <header className="mobile-live-header">
        <button
          type="button"
          className="mobile-back-btn"
          onClick={handleBack}
          aria-label="Back"
        >
          <MdArrowBackIos size={14} />
          <span className="mobile-header-title">Yantra Purchase</span>
        </button>

        <div className="mobile-header-tabs">
          <marquee direction="left" className="mobile-live-note">
            {liveSessionNote1 || "નોંધ:- યંત્રની ખરીદી 24 કલાક માન્ય રહેશે."}{" "}
            Program SN:#{sessionNo ?? initialValues.session_no ?? "—"}
          </marquee>
        </div>
        <div className="mobile-header-chips">
          <div className="mobile-header-time">
            <span className="mobile-header-time-label">Current Time</span>
            <span className="mobile-header-time-value">
              {currentTimeDisplay || "—"}
            </span>
          </div>
          {AMOUNT_CHIPS.map((amt) => (
            <button
              key={amt}
              type="button"
              className={`mobile-chip ${quickAmount === amt ? "active" : ""}`}
              onClick={() => setQuickAmount(amt)}
            >
              {amt}
            </button>
          ))}
          <div>
            <div className="mobile-header-game-id">
              {profile.username
                ? profile.username
                : profile.user_username
                  ? profile.user_username
                  : profile.game_id
                    ? profile.game_id
                    : "—"}
            </div>
          </div>
          {/* <button type="button" className="mobile-chip-ok">
            OK
          </button> */}
        </div>
      </header>
      <div ref={scrollContainerRef} className="mobile-scroll-container">
        {gameStoppedByAdmin && (
          <div className="mobile-game-stopped-banner" role="alert">
            Session stopped by admin. Please try again later.
          </div>
        )}
        <div ref={mainContentRef} className="mobile-live-main">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={async (
              values,
              { setSubmitting, resetForm, setFieldValue },
            ) => {
              // First submit shows confirmation modal; confirm triggers actual save.
              if (!placeBetConfirmProceedRef.current) {
                setPlaceBetConfirmData(buildPlaceBetConfirmData(values));
                setPlaceBetConfirmOpen(true);
                setSubmitting(false);
                return;
              }
              placeBetConfirmProceedRef.current = false;

              setSubmitting(true);

              if (remainingSeconds > 0 && remainingSeconds <= 10) {
                toast.error("Betting is closed in the last 10 seconds.");
                setSubmitting(false);
                return;
              }

              const wallet = Number(values.wallet_balance || 0);
              const totalBetSubmit = Number(values.total_bet || 0);
              if (wallet < 1) {
                toast.error("Insufficient wallet balance.");
                setSubmitting(false);
                return;
              }
              if (totalBetSubmit < 1) {
                toast.error("Please place a bet.");
                setSubmitting(false);
                return;
              }

              try {
                const response = await liveSSApi.savePlaceBetData(values);
                if (response.isSuccess) {
                  toast.success("Bet placed successfully");
                  if (sessionDbIdRef.current) {
                    try {
                      placedSessionDbIdsRef.current?.add(
                        Number(sessionDbIdRef.current),
                      );
                    } catch (_) {}
                  }
                  const newWalletBalanceRaw =
                    response?.walletData?.wallet_balance;
                  const newWalletBalance =
                    newWalletBalanceRaw != null &&
                    !Number.isNaN(Number(newWalletBalanceRaw))
                      ? Number(newWalletBalanceRaw)
                      : null;
                  const resetYantras = (values.yantra_list || []).map((y) => ({
                    ...y,
                    yantra_count: 0,
                  }));
                  resetForm({
                    values: {
                      ...values,
                      wallet_balance:
                        newWalletBalance != null
                          ? newWalletBalance
                          : values.wallet_balance,
                      total_bet: 0,
                      amount: 0,
                      yantra_list: resetYantras,
                    },
                  });
                  if (newWalletBalance == null) {
                    try {
                      const refresh = await liveSSApi.getLiveSessionData(
                        activeTabRef.current,
                      );
                      const wb = refresh?.walletData?.wallet_balance;
                      const pa =
                        refresh?.walletData?.paid_amount ??
                        refresh?.walletData?.paid_amoun;
                      if (wb != null && !Number.isNaN(Number(wb)))
                        setFieldValue("wallet_balance", Number(wb));
                      if (pa != null && !Number.isNaN(Number(pa)))
                        setFieldValue("paid_amount", Number(pa));
                    } catch (_) {}
                  }

                  // Close confirmation modal after successful save
                  setPlaceBetConfirmOpen(false);
                  setPlaceBetConfirmData(null);
                } else {
                  if (response?.userActiveStatus === 0) {
                    toast.error("User is not active");
                    logout();
                  } else {
                    toast.error(response?.message || "Failed to place bet");
                  }
                }
              } catch (error) {
                toast.dismiss();
                if (error?.response?.data?.userActiveStatus === 0) {
                  toast.error(
                    error?.response?.data?.message || "User is not active",
                  );
                  logout();
                } else {
                  toast.error(
                    error?.response?.data?.message || "Error placing bet",
                  );
                }
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values, isSubmitting, setFieldValue, submitForm }) => {
              const totalBet = Number(values.total_bet || 0);
              const showWinnerProcessingModal =
                !isFooterOnlyVisible &&
                Boolean(sessionId) &&
                remainingSeconds > 0 &&
                remainingSeconds <= 10;
              const isPlaceBetDisabled =
                isSubmitting ||
                remainingSeconds <= 0 ||
                remainingSeconds <= 10 ||
                values.session_status !== "RUNNING" ||
                totalBet === 0;

              return (
                <YantraImagesLoader values={values}>
                  {(yantraImages) => (
                    <Form className="mobile-live-form">
                      <SyncTotalBetAmount />

                      {placeBetConfirmOpen && (
                        <div
                          role="dialog"
                          aria-modal="true"
                          aria-label="Confirm place bet"
                          className="mobile-confirm-overlay"
                          onClick={() => {
                            if (isSubmitting) return;
                            setPlaceBetConfirmOpen(false);
                            setPlaceBetConfirmData(null);
                          }}
                        >
                          <div
                            className="mobile-confirm-modal"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="mobile-confirm-header">
                              <div className="mobile-confirm-title">
                                {placeBetConfirmData?.gameLabel ||
                                  "Yantra Session"}
                              </div>
                              <div className="mobile-confirm-meta">
                                User: {placeBetConfirmData?.userLabel ?? "—"}
                              </div>
                              <div className="mobile-confirm-meta-muted">
                                Purchase Time:{" "}
                                {placeBetConfirmData?.purchaseTime ?? "—"} Draw
                                Time: {placeBetConfirmData?.currentTime ?? "—"}-
                                {placeBetConfirmData?.drawTime ?? "—"}
                              </div>
                            </div>

                            <div className="mobile-confirm-body">
                              <div className="mobile-confirm-table-wrap">
                                <table className="mobile-confirm-table">
                                  <thead>
                                    <tr className="mobile-confirm-thead-row">
                                      <th className="mobile-confirm-th">
                                        Yantra
                                      </th>
                                      <th className="mobile-confirm-th center">
                                        Qty
                                      </th>
                                      <th className="mobile-confirm-th center">
                                        Price
                                      </th>
                                      <th className="mobile-confirm-th right">
                                        Amount
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(placeBetConfirmData?.rows || [])
                                      .length === 0 ? (
                                      <tr>
                                        <td
                                          colSpan={4}
                                          className="mobile-confirm-td"
                                        >
                                          No yantra selected.
                                        </td>
                                      </tr>
                                    ) : (
                                      (placeBetConfirmData.rows || []).map(
                                        (r, idx) => (
                                          <tr key={`${r.name}-${idx}`}>
                                            <td className="mobile-confirm-td name">
                                              {r.name}
                                            </td>
                                            <td className="mobile-confirm-td center">
                                              {r.qty}
                                            </td>
                                            <td className="mobile-confirm-td center">
                                              {Number(r.price || 0)}
                                            </td>
                                            <td className="mobile-confirm-td right">
                                              {Number(r.amount || 0)}
                                            </td>
                                          </tr>
                                        ),
                                      )
                                    )}
                                    <tr className="mobile-confirm-row-total">
                                      <td className="mobile-confirm-td">
                                        Total
                                      </td>
                                      <td className="mobile-confirm-td center">
                                        {placeBetConfirmData?.totalQty ?? 0}
                                      </td>
                                      <td className="mobile-confirm-td center"></td>
                                      <td className="mobile-confirm-td right">
                                        {formatMoney(
                                          placeBetConfirmData?.totalAmount ?? 0,
                                        )}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>

                              <div className="mobile-confirm-actions">
                                <button
                                  type="button"
                                  disabled={isSubmitting}
                                  onClick={() => {
                                    setPlaceBetConfirmOpen(false);
                                    setPlaceBetConfirmData(null);
                                  }}
                                  className="mobile-confirm-btn cancel"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  disabled={isSubmitting}
                                  onClick={() => {
                                    placeBetConfirmProceedRef.current = true;
                                    submitForm();
                                  }}
                                  className="mobile-confirm-btn confirm"
                                >
                                  {isSubmitting ? "Saving..." : "Confirm"}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {winCongratsOpen && (
                        <div
                          role="dialog"
                          aria-modal="true"
                          aria-label="Congratulations"
                          className="mobile-win-overlay"
                          onClick={() => {
                            setWinCongratsOpen(false);
                            setWinCongratsData(null);
                          }}
                        >
                          <div
                            className="mobile-win-modal"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="mobile-win-header">
                              <div className="mobile-win-title">
                                Congratulations
                              </div>
                              <div className="mobile-win-sub">
                                You won{" "}
                                <span className="mobile-win-amount">
                                  ₹{formatMoney(winCongratsData?.totalWin || 0)}
                                </span>
                              </div>
                              <div className="mobile-win-meta">
                                • Purchase: ₹{" "}
                                {formatMoney(
                                  winCongratsData?.totalPurchase || 0,
                                )}
                              </div>
                            </div>

                            <div
                              className="mobile-win-body"
                              style={{
                                background: `url(${tenorGif})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                backgroundRepeat: "no-repeat",
                                backgroundColor: "rgba(0, 0, 0, 0.6)",
                              }}
                            >
                              <div className="mobile-win-list">
                                {(winCongratsData?.slips || []).map(
                                  (s, idx) => (
                                    <div
                                      className="mobile-win-row"
                                      key={`${s?.placebet_uid || "slip"}-${idx}`}
                                    >
                                      <div className="mobile-win-uid">
                                        {s?.placebet_uid || "—"}
                                      </div>
                                      <div className="mobile-win-row-right">
                                        <span className="mobile-win-small">
                                          ₹{formatMoney(s?.amount || 0)}
                                        </span>
                                        <span className="mobile-win-small strong">
                                          ₹{formatMoney(s?.win_amount || 0)}
                                        </span>
                                      </div>
                                    </div>
                                  ),
                                )}
                              </div>

                              <button
                                type="button"
                                className="mobile-win-ok"
                                onClick={() => {
                                  setWinCongratsOpen(false);
                                  setWinCongratsData(null);
                                }}
                              >
                                OK
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {showWinnerProcessingModal && (
                        <div
                          role="dialog"
                          aria-modal="true"
                          aria-label="Winner processing"
                          style={{
                            position: "fixed",
                            inset: 0,
                            background: "rgba(0, 0, 0, 0.55)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 9999,
                            pointerEvents: "all",
                          }}
                          onClick={(e) => e.preventDefault()}
                        >
                          <div
                            style={{
                              width: "min(360px, calc(100vw - 32px))",
                              background: "#fff",
                              borderRadius: 12,
                              padding: "22px 20px",
                              boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
                              textAlign: "center",
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div style={{ fontWeight: 700, fontSize: 18 }}>
                              Winner processing
                            </div>
                            <div style={{ marginTop: 6, color: "#555" }}>
                              Betting is closed in the last 10 seconds.
                            </div>
                            <div style={{ marginTop: 10, color: "#111" }}>
                              Next round starts soon ({remainingSeconds}s)
                            </div>
                            <div
                              style={{
                                marginTop: 14,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 10,
                              }}
                            >
                              <svg
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                              >
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="9"
                                  fill="none"
                                  stroke="#667eea"
                                  strokeWidth="3"
                                  strokeLinecap="round"
                                  strokeDasharray="42 56"
                                >
                                  <animateTransform
                                    attributeName="transform"
                                    type="rotate"
                                    from="0 12 12"
                                    to="360 12 12"
                                    dur="0.9s"
                                    repeatCount="indefinite"
                                  />
                                </circle>
                              </svg>
                              <span style={{ color: "#333" }}>
                                Please wait...
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="mobile-live-content">
                        <div className="mobile-left">
                          {yantraLoading ? (
                            <div className="mobile-loader-wrap">
                              <Loader message="Loading..." />
                            </div>
                          ) : values.yantra_list.length === 0 ? (
                            <NoData
                              message="No Yantras Available"
                              subText="Please try again later"
                            />
                          ) : (
                            <FieldArray name="yantra_list">
                              {() => (
                                <div className="mobile-grid-wrap">
                                  <div className="mobile-grid-inner">
                                    {(yantraImages || values.yantra_list || [])
                                      .slice(0, 10)
                                      .map((yantra, idx) => {
                                        const imgSrc =
                                          yantra.imgSrc ??
                                          getYantraImage(yantra.yantra_img);
                                        return (
                                          <div
                                            key={yantra.yantra_id || idx}
                                            className="mobile-yantra-card"
                                            onClick={() => {
                                              const next =
                                                Number(
                                                  yantra.yantra_count || 0,
                                                ) + quickAmount;
                                              setFieldValue(
                                                `yantra_list[${idx}].yantra_count`,
                                                next,
                                              );
                                            }}
                                          >
                                            <div className="mobile-yantra-img-wrap">
                                              {imgSrc ? (
                                                <img
                                                  src={imgSrc}
                                                  alt={
                                                    yantra.yantra_name ||
                                                    "Yantra"
                                                  }
                                                />
                                              ) : (
                                                <div className="mobile-yantra-placeholder" />
                                              )}
                                            </div>
                                            <div className="mobile-yantra-name">
                                              {yantra.yantra_name || "Yantra"}
                                            </div>
                                            <div className="mobile-yantra-count">
                                              <Field
                                                name={`yantra_list[${idx}].yantra_count`}
                                                type="number"
                                                min="0"
                                                readOnly
                                                onClick={() => {
                                                  const next =
                                                    Number(
                                                      yantra.yantra_count || 0,
                                                    ) + quickAmount;
                                                  setFieldValue(
                                                    `yantra_list[${idx}].yantra_count`,
                                                    next,
                                                  );
                                                }}
                                              />
                                            </div>
                                          </div>
                                        );
                                      })}
                                  </div>
                                </div>
                              )}
                            </FieldArray>
                          )}

                          <p className="mobile-disclaimer">
                            {liveSessionNote2 ||
                              "Note: Yantra, Fengsui Item, and Rashi validity and purchase locations apply as per terms."}
                          </p>
                          <div>
                            <div className="mobile-footer-buttons">
                              <button
                                type="button"
                                className="mobile-footer-btn mobile-btn-clear"
                                onClick={() => {
                                  (values.yantra_list || []).forEach((_, i) =>
                                    setFieldValue(
                                      `yantra_list[${i}].yantra_count`,
                                      0,
                                    ),
                                  );
                                }}
                              >
                                Clear
                              </button>
                              <button
                                type="button"
                                className="mobile-footer-btn mobile-btn-all-draw"
                                onClick={() => routerHistory.push("/all-draw")}
                              >
                                All Draw
                              </button>
                              <button
                                type="submit"
                                disabled={isPlaceBetDisabled}
                                className="mobile-footer-btn mobile-btn-place-bet"
                              >
                                {isSubmitting ? "Placing..." : "Place Bet"}
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="mobile-right">
                          <div className="mobile-wallet-panel">
                            <div className="mobile-wallet-row">
                              <span>
                                <LuWallet className="me-1" /> Wallet
                              </span>
                              <span>
                                <MdCurrencyRupee />
                                {Number(values.wallet_balance || 0).toFixed(2)}
                              </span>
                            </div>
                            <div className="mobile-wallet-row">
                              <span>
                                <GrLineChart className="me-1" /> Winning
                              </span>
                              <span>
                                <MdCurrencyRupee />
                                {Number(values.winning_amount || 0).toFixed(2)}
                              </span>
                            </div>
                            <div className="mobile-wallet-row">
                              <span>
                                <TbReceiptDollar className="me-1" /> Last Win
                              </span>
                              <span>
                                <MdCurrencyRupee />
                                {Number(
                                  values.last_winning_amount || 0,
                                ).toFixed(2)}
                              </span>
                            </div>
                            {/*<button type="button" className="mobile-recharge-btn">
                        + Recharge
                      </button> */}
                          </div>
                          <div className="mobile-session-panel">
                            <div className="mobile-activity-row">
                              <span>Total Bet</span>
                              <span>{totalBet}</span>
                            </div>
                            <div className="mobile-activity-row">
                              <span>Amount</span>
                              <span>
                                ₹{Number(values.amount || 0).toFixed(2)}
                              </span>
                            </div>
                            <div className="mobile-activity-row">
                              <span>Draw Time</span>
                              <span>{values.draw_time || "—"}</span>
                            </div>
                            <div className="mobile-activity-row mobile-remaining">
                              <span>Remaining</span>
                              <span>{formatRemaining(remainingSeconds)}</span>
                            </div>
                            <div className="mobile-activity-row">
                              <span>Current</span>
                              <span>{currentTimeDisplay || "—"}</span>
                            </div>
                            <div className="mobile-activity-row">
                              <span>Session</span>
                              <span>#{values.session_no || "—"}</span>
                            </div>
                            <div className="mobile-activity-row">
                              <span>Session ID</span>
                              <span className="mobile-session-uid">
                                {values.session_uid || "—"}
                              </span>
                            </div>
                            <div className="mobile-activity-row">
                              <span>
                                <Link
                                  to="/place-bet-management"
                                  className="mobile-activity-link"
                                >
                                  <FaHistory className="mobile-activity-icon" />
                                  <span>View History</span>
                                </Link>
                              </span>
                              <span>
                                <Link
                                  to="/transaction-history"
                                  className="mobile-activity-link"
                                >
                                  <FaHistory className="mobile-activity-icon" />
                                  <span>View Transactions</span>
                                </Link>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Form>
                  )}
                </YantraImagesLoader>
              );
            }}
          </Formik>
        </div>
        {/* Last draw row - inside Form so it scrolls with content */}
        <footer ref={footerRef} className="mobile-live-footer">
          <div className="mobile-recent-draws">
            {history.flatMap((item) => {
              const endTime = item?.end_time
                ? moment.utc(item.end_time).local()
                : null;
              const dateLabel = endTime ? endTime.format("DD-MM-YYYY") : "";
              const timeLabel = endTime
                ? endTime.format(TIME_DISPLAY_FORMAT)
                : "";
              const winnerType = String(item?.winner_type || "").toUpperCase();
              const isJackpot = winnerType === "JACKPOT" ? true : false;
              const jackpotBadgeLabel =
                winnerType === "JACKPOT"
                  ? "Multi Winner"
                  : winnerType === "JACKPOTX"
                    ? (() => {
                        const raw = String(item?.winner_x || "").trim();
                        if (!raw) return "Jackpot";
                        if (/[xX]$/.test(raw)) return `Jackpot (${raw})`;
                        return `Jackpot(${raw}x)`;
                      })()
                    : null;
              const winners =
                Array.isArray(item?.winner_yantras) &&
                item.winner_yantras.length > 0
                  ? item.winner_yantras
                  : [
                      {
                        yantra_name: item?.yantra_name,
                        yantra_img: item?.yantra_img,
                        yantra_id: item?.winner_yantra,
                      },
                    ];

              if (isJackpot) {
                const winnerNames = winners
                  .map(
                    (w) =>
                      w?.yantra_name ||
                      (w?.yantra_id ? `Yantra ${w.yantra_id}` : ""),
                  )
                  .filter(Boolean);
                const namesLabel =
                  winnerNames.length > 0 ? winnerNames.join(" | ") : "-";
                return [
                  <div
                    key={`${item.session_id || item.id}-jackpot`}
                    className={`mobile-lastdraw-card ${winnerType === "JACKPOT" ? "mobile-lastdraw-card-jackpot" : ""}`}
                  >
                    <div className="mobile-lastdraw-top">
                      <div className="mobile-lastdraw-dot mobile-lastdraw-dot-jackpot">
                        <div className="mobile-lastdraw-dot-placeholder">
                          <span className="mobile-lastdraw-dot-placeholder-text">
                            Multi
                          </span>
                        </div>
                      </div>
                      <div className="mobile-lastdraw-datetime">
                        <span className="mobile-lastdraw-date">
                          {dateLabel}
                        </span>
                        <span className="mobile-lastdraw-time">
                          {timeLabel}
                        </span>
                      </div>
                    </div>
                    {jackpotBadgeLabel && (
                      <div
                        className={`mobile-lastdraw-winner-badge ${
                          winnerType === "JACKPOT" ? "jackpotx" : "jackpot"
                        }`}
                        title={jackpotBadgeLabel}
                      >
                        {jackpotBadgeLabel}
                      </div>
                    )}
                    <div className="mobile-lastdraw-divider" />
                    <div className="mobile-lastdraw-name mobile-lastdraw-name-list">
                      {namesLabel}
                    </div>
                  </div>,
                ];
              }

              return winners.map((w, wIdx) => {
                const nameLabel =
                  w?.yantra_name || `Yantra ${w?.yantra_id || ""}` || "Yantra";
                const imgSrc = w?.yantra_img
                  ? `${LIVE_YANTRA_IMAGE_URL}/${w.yantra_img}`
                  : `${LIVE_YANTRA_IMAGE_URL}/default_image.png`;
                return (
                  <div
                    key={`${item.session_id || item.id}-${w?.yantra_id ?? wIdx}`}
                    className={`mobile-lastdraw-card ${winnerType === "JACKPOTX" ? "mobile-lastdraw-card-jackpot" : ""}`}
                  >
                    <div className="mobile-lastdraw-top">
                      <div className="mobile-lastdraw-dot">
                        {imgSrc ? <img src={imgSrc} alt={nameLabel} /> : null}
                      </div>
                      <div className="mobile-lastdraw-datetime">
                        <span className="mobile-lastdraw-date">
                          {dateLabel}
                        </span>
                        <span className="mobile-lastdraw-time">
                          {timeLabel}
                        </span>
                      </div>
                    </div>
                    {winnerType === "JACKPOTX" && (
                      <div
                        className={`mobile-lastdraw-winner-badge ${
                          winnerType === "JACKPOTX" ? "jackpot" : "jackpot"
                        }`}
                        title={
                          winnerType === "JACKPOTX" ? "Jackpot" : "Jackpot"
                        }
                      >
                        {jackpotBadgeLabel}
                      </div>
                    )}
                    <div className="mobile-lastdraw-divider" />
                    <div className="mobile-lastdraw-name">{nameLabel}</div>
                  </div>
                );
              });
            })}
          </div>
        </footer>
      </div>
    </div>
  );
};

export default MobileLiveSession;
